/**
 * Shows a small classification badge next to an employee name across the
 * admin app (Attendance, Qiwa, Payroll, Leaves, etc.) so administrators
 * can tell at a glance whether the row is a regular on-site employee, a
 * flexible-schedule worker, a contracted "أجير" worker, a salaried doctor,
 * or a percentage-based doctor — without opening the employee profile.
 *
 * Props mirror the underlying user document fields exactly so callers can
 * spread them in: `{...emp}`.
 */
const PALETTES = {
  doctor_salary:    { ar: 'طبيب راتب',  en: 'Doctor · Salary',    cls: 'bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]' },
  doctor_percent:   { ar: 'طبيب نسبة',  en: 'Doctor · % share',    cls: 'bg-[#FCE7F3] text-[#9D174D] border-[#FBCFE8]' },
  contracted:       { ar: 'أجير',       en: 'Contracted',          cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  flexible:         { ar: 'مرن',        en: 'Flexible',            cls: 'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]' },
  remote:           { ar: 'عن بُعد',    en: 'Remote',              cls: 'bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]' },
  consultant:       { ar: 'استشاري',    en: 'Consultant',          cls: 'bg-[#F3E8FF] text-[#6B21A8] border-[#E9D5FF]' },
};

/**
 * Resolve which badge (if any) applies to this employee. We deliberately
 * pick a SINGLE badge per row — the most specific classification wins so
 * the UI doesn't become noisy. Order: doctor → contracted → employment_type.
 */
export const resolveEmployeeBadge = (emp = {}) => {
  if (emp.is_doctor) {
    if (emp.doctor_type === 'percentage') return PALETTES.doctor_percent;
    if (emp.doctor_type === 'salary') return PALETTES.doctor_salary;
    return PALETTES.doctor_salary; // default fallback for legacy rows
  }
  if (emp.is_contracted_worker) return PALETTES.contracted;
  const et = (emp.employment_type || '').toLowerCase();
  if (et === 'flexible') return PALETTES.flexible;
  if (et === 'remote') return PALETTES.remote;
  if (et === 'consultant') return PALETTES.consultant;
  return null;
};

export const EmployeeTypeBadge = ({ employee, language = 'ar', className = '' }) => {
  const p = resolveEmployeeBadge(employee || {});
  if (!p) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.cls} ${className}`}
      data-testid="employee-type-badge"
    >
      {language === 'ar' ? p.ar : p.en}
    </span>
  );
};

export default EmployeeTypeBadge;
